export enum ComponentEnum {
  VIDEO = "Video",
  IMAGE = "Image",
  TEXT = "Text",
  INPUT = "Input",
  MULTIPLE_CHOICE = "Multiple Choice",
  RATING_SCALE = "Rating Scale",
  TOGGLE_TEXT = "Toggle Text",
  LOGIC_COMPONENT = "Logic Component",
  LESSON_LINK = "Lesson Link",
  USER_LIST = "User List",
  CUSTOM_LINK = "Custom Link",
  ACTION_DETAIL = "Action Detail",
  DATE_TIME = "Date Time",
  PRIORITY_LIST = "Priority List",
  CALLOUT_BOX = "Callout Box",
  FOR_EACH = "For Each",
  TOGGLE = "Toggle",
  ADD_TO_CALENDAR = "Add to Calendar",
}

/** Set of component type strings supported by this build (from enum). DB may contain types from other envs (e.g. stage-only); ignore those. */
export const SUPPORTED_COMPONENT_TYPES = new Set<string>(
  Object.values(ComponentEnum),
);

export function isSupportedComponentType(type: string): boolean {
  return SUPPORTED_COMPONENT_TYPES.has(type);
}

export const inputComponents = new Set([
  ComponentEnum.INPUT,
  ComponentEnum.MULTIPLE_CHOICE,
  ComponentEnum.RATING_SCALE,
  ComponentEnum.USER_LIST,
]);

export enum SignInStatus {
  SUCCESS = "SUCCESS",
  UNAUTHORIZED = "UNAUTHORIZED",
  NEW_PASSWORD_REQUIRED = "NEW_PASSWORD_REQUIRED",
  PENDING_CONFIRMATION = "PENDING_CONFIRMATION",
}

export interface SignInRes {
  status: SignInStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userSession?: any;
  // eslint-disable-next-line
  error?: any;
}

export enum CategoryEnum {
  CAREER = "Career",
  CLOSE_RELATIONSHIPS = "Close Relationships",
  GROWTH_AND_WELL_BEING = "Growth & Well-being",
  FAMILY_AND_PARENTING = "Family & Parenting",
}

export enum ModuleStatus {
  Draft = "Draft",
  Published = "Published",
  Archived = "Archived",
  Coming_Soon = "Coming Soon",
}

export enum FeedbackValue {
  NOT_AT_ALL_USEFUL = "not_at_all_useful",
  MODERATELY_USEFUL = "moderately_useful",
  VERY_USEFUL = "very_useful",
}

export enum ResponseEnum {
  ACTIVITY = "Activity_New",
  REFLECTION = "Reflection",
  SECTION_RECAP = "Section Recap",
}

export enum GeneralFeedbackTypeEnum {
  PAGE_FEEDBACK = "page_feedback",
  LESSON_FEEDBACK = "lesson_feedback",
  GENERAL_FEEDBACK = "general_feedback",
  BUG_REPORT = "bug_report",
  FEATURE_REQUEST = "feature_request",
}

export enum ImageTypeEnum {
  ICON = "Icon",
  ILLUSTRATION = "Illustration",
  THUMBNAIL = "Thumbnail",
}

export enum DateTimeTypeEnum {
  DATE = "Date",
  TIME = "Time",
  DATE_TIME = "Date & Time",
}

export enum BookmarkTypeEnum {
  MODULE = "module",
  PAGE = "page",
}

export enum CognitoPoolEnum {
  STAGING = "staging",
  PRODUCTION = "production",
}

export interface ComponentI {
  _id?: string;
  type: ComponentEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
}

export interface PageI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  components: ComponentI[];
  keyTakeaways?: string;
  freezeVariablesFromPreviousPage?: string[];
  isDeleted?: boolean;
  deletionRequestedAt?: string | null;
  archivedAt?: string | null;
  isToolboxTool?: boolean;
}

export interface ImageI {
  _id?: string;
  src: string;
  key: string;
  name: string;
  alt: string;
  type: ImageTypeEnum;
}

export enum BottomNavigationBarType {
  MULTI_PAGE = "Multi Page",
  NONE = "None",
  SINGLE_PAGE = "Single Page",
}

export interface LessonI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  slug?: string;
  icon: ImageI;
  pages: PageI[];
  bottomNavigationBarType?: BottomNavigationBarType;
}

export interface ModuleI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  slug?: string;
  subtitle?: string;
  description: string;
  color: string;
  icon: ImageI;
  thumbnail?: ImageI | null;
  lessons: LessonI[];
  categories: CategoryEnum[];
  status: ModuleStatus;
  trailerVideoUrl?: string;
  trailerTranscript?: string;
}

export interface RecapI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content?: any[];
  module: ModuleI | string;
  lesson: LessonI | string;
  page?: PageI | string;
  completed?: boolean;
  pages_visited?: string[];
}

export interface ResponseI {
  _id?: string;
  module?: ModuleI | string;
  lesson?: LessonI | string;
  page?: PageI | string;
  title: string;
  keyTakeaway?: string;
  type: ResponseEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  isCompletion?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ResponsesGroupedByLessonI {
  responses: ResponseI[];
  lesson: LessonI;
}

export interface BookmarkI {
  _id?: string;
  userSub?: string;
  module?: ModuleI;
  lesson?: LessonI;
  page?: PageI;
  type: BookmarkTypeEnum;
  createdAt?: Date;
  updatedAt?: Date;
  lastActive?: Date;
  // Optional frozen state captured when bookmarking a page
  frozenComponents?: ComponentI[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frozenAnswers?: Record<string, any>;
}

export enum AnalyticsCategoryEnum {
  Lesson = "Lesson",
  Module = "Module",
  Auth = "Auth",
  Toolbox = "Toolbox",
  Video = "Video",
  Page = "Page",
}
export interface AnalyticsEvent {
  eventName?: string;
  category: AnalyticsCategoryEnum;
  elementId?: string;
  additionalDetails?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface FeedbackI {
  _id?: string;
  userSub: string;
  lessonId?: string;
  moduleId?: string;
  pageNumber?: number;
  pageUrl?: string;
  feedbackType?: GeneralFeedbackTypeEnum;
  feedback?: FeedbackValue;
  subFeedback?: string[];
  otherFeedback?: string;
  canContact?: boolean;
  isResolved?: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
  createdAt: Date;
  updatedAt?: Date;
  userProfile?: {
    segments?: string[] | null;
    email?: string;
    username?: string;
  };
}

export interface LessonRatingI {
  lessonId: string;
  count: number;
  average: number;
  subFeedbackAverages?: {
    [key: string]: number;
  };
  subFeedbackCounts?: {
    [key: string]: number;
  };
}

export enum UserRolesEnum {
  USER = "user",
  ADMIN = "admin",
}

export enum UserSegmentEnum {
  TEAM = "team",
  BETA_TESTERS = "betatesters",
  ADVISORY_BOARD = "advisoryboard",
  EVALUATOR = "evaluator",
  TEST_ACCOUNT = "testaccount",
  ASU_CALM = "asucalm",
  CBIZ = "cbiz",
  ASU = "asu",
  USER_GROUP_A = "usergroupa",
  USER_GROUP_B = "usergroupb",
  FRIENDS_AND_FAMILY = "friendsandfamily",
  NO_SEGMENT = "nosegment",
  NO_DATA = "nodata",
}

export interface UserPreferences {
  saveAnswersToLog: boolean;
  askEveryTime: boolean;
  dismissedTutorials: string[];
  emailConsentDate?: Date;
  emailAccountIssues?: boolean;
  emailFeedbackRequests?: boolean;
  emailNewsUpdates?: boolean;
  videoPlaybackSpeed?: number;
  autoplayVideos?: boolean;
  videoVolume?: number;
}

export interface ToolboxI {
  _id?: string;
  userSub: string;
  module: string;
  skillsReadAt?: Date;
  actionsReadAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ToolboxQuestionContentInput {
  label?: string;
  placeholder?: string;
}

export interface ToolboxQuestionContentMultipleChoice {
  label?: string;
  choices: string[];
  msq?: boolean;
}

export interface ToolboxQuestionContentRatingScale {
  label?: string;
  scale: number;
  minLabel?: string;
  maxLabel?: string;
}

export type ToolboxQuestionContent =
  | ToolboxQuestionContentInput
  | ToolboxQuestionContentMultipleChoice
  | ToolboxQuestionContentRatingScale;

export interface InputQuestionI {
  _id?: string;
  questionId: string;
  variableName: string;
  type: "input";
  content: ToolboxQuestionContentInput;
}

export interface MultipleChoiceQuestionI {
  _id?: string;
  questionId: string;
  variableName: string;
  type: "multiple_choice";
  content: ToolboxQuestionContentMultipleChoice;
}

export interface RatingScaleQuestionI {
  _id?: string;
  questionId: string;
  variableName: string;
  type: "rating_scale";
  content: ToolboxQuestionContentRatingScale;
}

export interface UnknownQuestionI {
  _id?: string;
  questionId: string;
  variableName: string;
  type: string;
  content: ToolboxQuestionContent;
}

export type ToolboxQuestionI =
  | InputQuestionI
  | MultipleChoiceQuestionI
  | RatingScaleQuestionI;

export interface ToolboxCheckinDefinitionI {
  _id: string;
  topicId: string;
  version: number;
  isActive: boolean;
  displayName?: string | null;
  ratingEnabled: boolean;
  ratingScale?: number;
  ratingLabel?: string;
  questions: ToolboxQuestionI[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ToolboxProfileDefinitionI {
  _id: string;
  topicId: string;
  version: number;
  /** Admin list order; when absent, sort by `version`. */
  sortOrder?: number;
  isActive: boolean;
  displayName?: string | null;
  questions: ToolboxQuestionI[];
  createdAt?: string;
  updatedAt?: string;
}

export type ToolboxAnswerValue = string | number | string[];
/** Keys are each profile question's `variableName`. */
export type ToolboxAnswers = Record<string, ToolboxAnswerValue>;

export interface UserToolboxProfileI {
  _id: string;
  userSub: string;
  topicId: string;
  definitionId: string;
  /** Keys are `variableName` for each question on the linked definition. */
  answers: ToolboxAnswers;
  createdAt?: string;
  updatedAt?: string;
}

export interface AccessCodeI {
  _id?: string;
  accessCode: string;
  limitCount?: number;
  redeemedCount: number;
  segment: UserSegmentEnum;
  expirationDate?: Date;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AccessCodeAnalyticsUser {
  userSub: string;
  username: string;
  email: string;
  redeemedAt: Date;
}

export interface AccessCodeAnalytics {
  accessCode: {
    id: string;
    code: string;
    segment: UserSegmentEnum;
    limitCount?: number;
    redeemedCount: number;
    isActive: boolean;
    expirationDate?: Date;
    isExpired: boolean;
    daysUntilExpiration: number | null;
    createdAt: Date;
    updatedAt: Date;
  };
  redemptionStats: {
    totalRedemptions: number;
    limitReached: boolean;
    remainingRedemptions: number | null;
    percentageUsed: number | null;
  };
  users: AccessCodeAnalyticsUser[];
}

export enum AnnouncementTypeEnum {
  INFO = "info",
  UPDATE = "update",
  FEATURE = "feature",
}

export enum AnnouncementPriorityEnum {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export interface AnnouncementActionI {
  label: string;
  url?: string;
  variant?: "primary" | "secondary";
}

export interface AnnouncementI {
  _id: string;
  title: string;
  message: string;
  type: AnnouncementTypeEnum;
  priority: AnnouncementPriorityEnum;
  actions?: AnnouncementActionI[];
  expiresAt?: Date;
  startsAt?: Date;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserAnnouncementInteractionI {
  _id: string;
  announcementId: string;
  userSub: string;
  viewedAt?: Date;
  dismissedAt?: Date;
  actionClicked?: string;
  actionClickedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ToolboxPlanI {
  _id: string;
  topicId: string;
  goal: string;
  cue: string;
  timeframe: string;
  createdAt?: string;
  updatedAt?: string;
}
